import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useMemo, useState } from 'react';
import type { To } from 'react-router';
import { useLocation } from 'react-router';

import {
  So5FixtureEvent,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { DateOrFixturePicker } from '@sorare/core/src/components/lobby/DateOrFixturePicker';
import { ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_LINEUPS } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { usePaginatedQuery } from '@sorare/core/src/hooks/graphql/usePaginatedQuery';
import { findLastIndex } from '@sorare/core/src/lib/arrays';

import { isFixtureOpened } from 'lib/so5';

import { LobbyFixturePickerItem } from '../LobbyFixturePickerItem';
import { PastFixtureSelectorFooter } from '../PastFixtureSelectorFooter';
import type {
  FixtureSelectorQuery,
  FixtureSelectorQueryVariables,
  FixtureSelector_so5Fixture,
} from './__generated__/index.graphql';

type Props = {
  currentFixture: Nullable<FixtureSelector_so5Fixture>;
  disableLiveAndPast?: boolean;
  disableUpcoming?: boolean;
  navigateTo?: (fixture: string) => To;
  withMask?: boolean;
  onFixtureChanged?: (fixtureName: string) => void;
  pickerSize?: 'small' | 'big';
  withMyCircleFooter?: boolean;
};
type Fixture =
  | FixtureSelectorQuery['so5']['so5Fixtures']['nodes'][0]
  | FixtureSelector_so5Fixture;

const FIXTURE_PAGE_SIZE = 40;

const FIXTURE_SELECTOR_QUERY = gql`
  query FixtureSelectorQuery(
    $cursor: String
    $pageSize: Int!
    $aasmStates: [String!]
    $sport: Sport!
    $eventType: So5FixtureEvent
  ) {
    so5 {
      so5Fixtures(
        aasmStates: $aasmStates
        first: $pageSize
        after: $cursor
        sport: $sport
        eventType: $eventType
      ) {
        nodes {
          slug
          type
          displayName
          ...LobbyFixturePickerItem_so5Fixture
          ...PastFixtureSelectorFooter_so5Fixture
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
    }
  }
  ${LobbyFixturePickerItem.fragments.so5Fixture}
  ${PastFixtureSelectorFooter.fragments.so5Fixture}
` as TypedDocumentNode<FixtureSelectorQuery, FixtureSelectorQueryVariables>;

export const FixtureSelector = ({
  currentFixture,
  disableLiveAndPast,
  disableUpcoming,
  navigateTo,
  withMask,
  onFixtureChanged,
  pickerSize = 'small',
  withMyCircleFooter = false,
}: Props) => {
  const location = useLocation();
  const { sport, generateSportContextPath } = useSportContext();
  const { data, loadMore } = usePaginatedQuery(FIXTURE_SELECTOR_QUERY, {
    connection: 'So5FixtureConnection',
    variables: {
      sport: sport || Sport.FOOTBALL,
      pageSize: FIXTURE_PAGE_SIZE,
      aasmStates: [
        ...(disableUpcoming ? [] : ['opened']),
        'started',
        'computed',
        'closed',
        'open',
      ],
      eventType: currentFixture?.type || So5FixtureEvent.CLASSIC,
    },
  });
  const so5Fixtures = data?.so5.so5Fixtures;
  const { endCursor, hasNextPage: hasMore = false } =
    so5Fixtures?.pageInfo || {};

  const fetchMore = useMemo(() => {
    if (!hasMore || disableLiveAndPast) {
      return undefined;
    }
    return () => {
      if (endCursor) {
        loadMore(false, { cursor: endCursor });
      }
    };
  }, [loadMore, endCursor, hasMore, disableLiveAndPast]);

  const [inputValue, setInputValue] = useState<Fixture | undefined>(
    location.state?.fixture
  );
  if (
    currentFixture &&
    (!inputValue || inputValue.slug !== currentFixture.slug)
  ) {
    setInputValue(currentFixture);
  }
  const fixturesList = so5Fixtures?.nodes || [];
  const upcomingFixtureIndex = findLastIndex(fixturesList, isFixtureOpened);

  return (
    inputValue && (
      <DateOrFixturePicker
        loading={!data}
        renderItem={({ item: so5Fixture, index }) => (
          <LobbyFixturePickerItem
            so5Fixture={so5Fixture}
            isUpcoming={index === upcomingFixtureIndex}
            disabled={!!disableLiveAndPast && !isFixtureOpened(so5Fixture)}
            onClick={() => {
              setInputValue(so5Fixture);
              onFixtureChanged?.(so5Fixture.displayName);
            }}
            to={
              navigateTo?.(so5Fixture.slug) ??
              generateSportContextPath(
                ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_LINEUPS,
                {
                  params: {
                    eventType: so5Fixture.type.toLowerCase(),
                    fixture: so5Fixture.slug,
                  },
                }
              )
            }
            pastFixtureFooter={
              <PastFixtureSelectorFooter
                so5Fixture={so5Fixture}
                withMyCircleFooter={withMyCircleFooter}
              />
            }
            selected={so5Fixture.slug === inputValue.slug}
            variant={pickerSize}
          />
        )}
        renderPlaceholder={() => (
          <LobbyFixturePickerItem
            to="."
            loading
            so5Fixture={null}
            variant={pickerSize}
          />
        )}
        value={inputValue}
        items={fixturesList}
        loadMore={fetchMore}
        withMask={withMask}
      />
    )
  );
};

FixtureSelector.fragments = {
  so5Fixture: gql`
    fragment FixtureSelector_so5Fixture on So5Fixture {
      slug
      type
      displayName
      ...LobbyFixturePickerItem_so5Fixture
      ...PastFixtureSelectorFooter_so5Fixture
    }
    ${PastFixtureSelectorFooter.fragments.so5Fixture}
    ${LobbyFixturePickerItem.fragments.so5Fixture}
  ` as TypedDocumentNode<FixtureSelector_so5Fixture>,
};
